import React, { useCallback, useEffect, useRef } from 'react';
import Lenis from 'lenis';

const SCROLL_OFFSET = -90;

interface Props {
  location: {
    pathname: string;
    hash: string;
  };
}

const getTargetElement = (hash: string) => {
  const id = decodeURIComponent(hash.replace(/^#/, ''));
  return id ? document.getElementById(id) : null;
};

const SmoothScroll: React.FC<Props> = ({ location }) => {
  const lenisRef = useRef<Lenis | null>(null);

  const scrollToHash = useCallback((hash: string) => {
    const target = getTargetElement(hash);

    if (!target) {
      return;
    }

    if (lenisRef.current) {
      lenisRef.current.scrollTo(target, {
        offset: SCROLL_OFFSET,
      });
    } else {
      target.scrollIntoView();
    }

    target.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return undefined;
    }

    const lenis = new Lenis({
      duration: 1.15,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      anchors: {
        offset: SCROLL_OFFSET,
      },
    });

    let animationFrame = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      animationFrame = requestAnimationFrame(raf);
    };

    lenisRef.current = lenis;
    animationFrame = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(animationFrame);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
        return;
      }

      if (!(event.target instanceof Element)) {
        return;
      }

      const link = event.target.closest('a[href]') as HTMLAnchorElement | null;

      if (!link) {
        return;
      }

      const url = new URL(link.href);

      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) {
        return;
      }

      const target = getTargetElement(url.hash);

      if (!target) {
        return;
      }

      event.preventDefault();

      if (window.location.hash !== url.hash) {
        window.history.pushState(null, '', `${url.pathname}${url.search}${url.hash}`);
      }

      scrollToHash(url.hash);
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [scrollToHash]);

  useEffect(() => {
    if (!location.hash) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      scrollToHash(location.hash);
    }, 50);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [location.hash, location.pathname, scrollToHash]);

  return null;
};

export default SmoothScroll;
